import { Button } from '@alison/ui';
import CheckIcon from '@mui/icons-material/Check';
import RefreshIcon from '@mui/icons-material/Refresh';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { MouseEvent, ReactNode } from 'react';
import { useState } from 'react';
import { useSlackChannelManager } from '@/lib/notifications.hooks.js';

export function ChannelPicker({
  current,
  onReconnect,
}: {
  current: string;
  onReconnect: () => void;
}): ReactNode {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const open = anchor !== null;
  const channelManager = useSlackChannelManager(open);
  const channels = channelManager.channels;
  const forbidden = channelManager.error?.data?.code === 'FORBIDDEN';
  const missingScope = channelManager.error?.message.includes('missing_scope') === true;

  function openMenu(event: MouseEvent<HTMLButtonElement>): void {
    setAnchor(event.currentTarget);
  }

  function selectChannel(channelId: string): void {
    void channelManager
      .saveChannel(channelId)
      .then(() => setAnchor(null))
      .catch(() => undefined);
  }

  return (
    <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
      <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.primary' }}>
        <Typography component="span" sx={{ color: 'text.secondary' }}>
          #
        </Typography>
        {current}
      </Typography>
      <Button
        id="slack-channel-picker-button"
        size="small"
        variant="text"
        aria-haspopup="menu"
        aria-expanded={open ? 'true' : undefined}
        aria-controls={open ? 'slack-channel-picker-menu' : undefined}
        onClick={openMenu}
        sx={{ minWidth: 0, p: 0 }}
      >
        Change
      </Button>
      <Menu
        id="slack-channel-picker-menu"
        anchorEl={anchor}
        open={open}
        onClose={() => setAnchor(null)}
        slotProps={{
          list: {
            'aria-labelledby': 'slack-channel-picker-button',
            sx: { minWidth: 230, maxHeight: 280, py: 1 },
          },
          paper: { sx: { mt: 1, border: '1px solid', borderColor: 'divider', boxShadow: 7 } },
        }}
      >
        {channelManager.isPending ? (
          <MenuItem disabled>
            <CircularProgress size={14} sx={{ mr: 1 }} /> Loading channels…
          </MenuItem>
        ) : channelManager.isError ? (
          <MenuItem onClick={missingScope ? onReconnect : () => void channelManager.refetch()}>
            <ListItemIcon>
              <RefreshIcon fontSize="small" />
            </ListItemIcon>
            {forbidden
              ? 'Only org admins can change the channel'
              : missingScope
                ? 'Reconnect Slack to update permissions'
                : 'Retry loading channels'}
          </MenuItem>
        ) : channels.length === 0 ? (
          <MenuItem disabled>No channels found</MenuItem>
        ) : (
          channels.map((channel) => {
            const selected = channel.name === current;
            return (
              <MenuItem
                key={channel.id}
                role="menuitemradio"
                aria-checked={selected}
                aria-label={`#${channel.name}`}
                selected={selected}
                disabled={channelManager.isSaving}
                onClick={() => selectChannel(channel.id)}
                sx={{ minHeight: 36, fontSize: 12, fontWeight: selected ? 700 : 500 }}
              >
                <Typography sx={{ flex: 1, fontSize: 'inherit', fontWeight: 'inherit' }}>
                  #{channel.name}
                </Typography>
                {selected && <CheckIcon sx={{ ml: 2, fontSize: 15, color: 'primary.main' }} />}
              </MenuItem>
            );
          })
        )}
        {channelManager.saveError && (
          <Alert severity="error">Could not save the channel. Please try again.</Alert>
        )}
      </Menu>
    </Stack>
  );
}
